import { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";

import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import {
  BCPSquarePlus,
  BCPTrash,
  BCPXmark,
} from "../../../../components/icons/bcp/BCPIcons";

import CountrySelect from "../../../../components/utilities/CountrySelect";
import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import { postNotification } from "../../../../components/utilities/Notifications";
import ListResponsive from "../../../../components/other/ListResponsive";
import { postData } from "../../../../components/utilities/UtilityFunctions";

export default function NotificationsTab(props) {
  const { user, setUser, authContext } = props;
  const [processing, setProcessing] = useState(false);
  const [followZipCodeOpen, setFollowZipCodeOpen] = useState(false);

  const unfollowZipCode = async (zipCode) => {
    try {
      setProcessing(true);
      var followingZipCodes = await postData(
        `/users/${user.id}/followZipCode`,
        {
          body: {
            unfollow: true,
            zipCode: zipCode,
          },
        }
      );
      setUser({
        ...user,
        followingZipCodes: followingZipCodes || [],
      });
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
  };

  return (
    <>
      <Formik
        initialValues={{
          sendNotifications: user?.sendNotifications || false,
        }}
        validationSchema={Yup.object().shape({
          sendNotifications: Yup.boolean(),
        })}
        onSubmit={async (values) => {
          try {
            setProcessing(true);
            var body = {
              ...values,
              username: values.email,
            };
            await postData(`/users/${user.id}`, {
              body: {
                set: body,
              },
            });
            postNotification("success", "User updated");
          } catch (err) {
            postNotification("error", err);
          }
          setProcessing(false);
        }}
      >
        {({ errors, handleSubmit, setFieldValue, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h5">Events Near Me</Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="sendNotifications"
                      checked={values.sendNotifications}
                      defaultChecked={false}
                      onChange={async (e) => {
                        try {
                          let sendNotifications = !values.sendNotifications;
                          setFieldValue("sendNotifications", sendNotifications);
                          setProcessing(true);
                          var body = {
                            sendNotifications: sendNotifications,
                          };
                          await postData(`/users/${user.id}`, {
                            body: {
                              set: body,
                            },
                          });
                          postNotification("success", "User updated");
                        } catch (err) {
                          postNotification("error", err);
                        }
                        setProcessing(false);
                      }}
                    />
                  }
                  label="I would like to hear about events near me"
                />

                {Boolean(
                  touched.sendNotifications && errors.sendNotifications
                ) && (
                  <FormHelperText sendNotifications>
                    {errors.sendNotifications}
                  </FormHelperText>
                )}
                <FormHelperText>
                  <Typography>
                    Receive email notifications on events near your selected zip
                    codes.
                  </Typography>

                  <Typography>
                    This list will also feature zip codes of previously attended
                    events.
                  </Typography>

                  <Typography>
                    These email notifications are sent weekly on Thursday
                    mornings (PST).
                  </Typography>
                </FormHelperText>
              </Grid>

              {(authContext?.authState?.identity?.isAdmin ||
                authContext?.authState?.identity?.isTester) && (
                <>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      color="success"
                      variant="outlined"
                      onClick={async () => {
                        try {
                          setProcessing(true);
                          var response = await postData(
                            `/users/${user.id}/sendWeeklyEventsEmail`,
                            { body: {} }
                          );
                          if (response?.message)
                            postNotification(
                              response?.message ? "info" : "success",
                              response?.message ||
                                "Email sent to notification queue"
                            );
                        } catch (err) {
                          postNotification("error", err);
                        }
                        setProcessing(false);
                      }}
                    >
                      Send Events Near Me Email
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      color="success"
                      variant="outlined"
                      onClick={async () => {
                        try {
                          setProcessing(true);
                          var response = await postData(
                            `/users/${user.id}/sendUserOnboardingEmail`,
                            { body: {} }
                          );
                          if (response?.message)
                            postNotification(
                              response?.message ? "info" : "success",
                              response?.message ||
                                "Email sent to notification queue"
                            );
                        } catch (err) {
                          postNotification("error", err);
                        }
                        setProcessing(false);
                      }}
                    >
                      Send User Onboarding Email
                    </Button>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <ListResponsive
                  originalList={user?.followingZipCodes || []}
                  doPages
                  initialRowsPerPage={24}
                  rowProps={{
                    headings: (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          p: "2px",
                          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                          borderRadius: "4px 4px 0 0",
                          backgroundColor: "table.headerBackground",
                          color: "table.headerColor",
                        }}
                      >
                        <ZipCodeRow
                          heading
                          setFollowZipCodeOpen={setFollowZipCodeOpen}
                        />
                      </Grid>
                    ),
                    mapFunction: (row, index) => (
                      <Grid
                        item
                        xs={12}
                        key={index}
                        sx={{
                          p: "2px",
                          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        }}
                      >
                        <ZipCodeRow
                          row={row}
                          unfollowZipCode={unfollowZipCode}
                        />
                      </Grid>
                    ),
                  }}
                />
              </Grid>
              <Modal
                disableAutoFocus
                open={followZipCodeOpen}
                onClose={() => {
                  setFollowZipCodeOpen(false);
                }}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                  timeout: 500,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: ["95vw", "85vw", 600],
                    maxHeight: "95vh",
                    bgcolor: "background.paper",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 24,
                    p: 4,
                  }}
                >
                  <IconButton
                    aria-label="cancel"
                    color="error"
                    sx={{ position: "absolute", top: "4px", right: "4px" }}
                    onClick={() => {
                      setFollowZipCodeOpen(false);
                    }}
                  >
                    <BCPXmark />
                  </IconButton>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      pb: 1,
                      mb: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    }}
                  >
                    <Grid item xs={12}>
                      <Typography variant="h4" component="h2">
                        Follow Zip Code
                      </Typography>
                    </Grid>
                  </Grid>
                  <Formik
                    initialValues={{
                      zipCode: "",
                      countryCode: "",
                    }}
                    validationSchema={Yup.object().shape({
                      zipCode: Yup.string().max(255).required("Required"),
                      countryCode: Yup.string().max(255).required("Required"),
                    })}
                    onSubmit={async (values) => {
                      try {
                        setProcessing(true);

                        var followingZipCodes = await postData(
                          `/users/${user.id}/followZipCode`,
                          {
                            body: values,
                          }
                        );
                        setUser({
                          ...user,
                          followingZipCodes: followingZipCodes || [],
                        });
                        postNotification("success", "Following new Zip Code");
                        setFollowZipCodeOpen(false);
                      } catch (err) {
                        postNotification("error", err);
                      }
                      setProcessing(false);
                    }}
                  >
                    {({
                      errors,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      touched,
                      values,
                      setFieldValue,
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <CountrySelect
                              value={values.countryCode}
                              name="countryCode"
                              setFieldValue={setFieldValue}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              error={Boolean(touched.zipCode && errors.zipCode)}
                              helperText={touched.zipCode && errors.zipCode}
                              name="zipCode"
                              value={values.zipCode}
                              label="Zip Code"
                              variant="outlined"
                              color="primary"
                              required
                              fullWidth
                              onBlur={handleBlur}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Button
                              onClick={() => setFollowZipCodeOpen(false)}
                              fullWidth
                              type="button"
                              color="error"
                              variant="outlined"
                            >
                              Cancel
                            </Button>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Button
                              fullWidth
                              type="submit"
                              color="success"
                              variant="contained"
                            >
                              Follow
                            </Button>
                          </Grid>
                        </Grid>
                      </form>
                    )}
                  </Formik>
                </Box>
              </Modal>
            </Grid>
          </form>
        )}
      </Formik>

      {Boolean(processing) && <ModalLoadingFull />}
    </>
  );
}

const ZipCodeRow = (props) => {
  const { heading, row, unfollowZipCode, setFollowZipCodeOpen } = props;
  return (
    <Grid
      container
      alignItems="center"
      spacing={1}
      sx={{
        fontSize: "14px",
        overflow: "hidden",
      }}
    >
      <Grid item xs={10}>
        {heading ? "Zip Code" : row.name}
      </Grid>

      <Grid item xs={2} textAlign="right">
        {heading ? (
          <IconButton
            aria-label="follow"
            color="inherit"
            onClick={async () => {
              setFollowZipCodeOpen(true);
            }}
          >
            <BCPSquarePlus />
          </IconButton>
        ) : (
          <IconButton
            aria-label="unfollow"
            color="error"
            onClick={async () => {
              unfollowZipCode(row.zipCode);
            }}
          >
            <BCPTrash />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};
